import LearnLayout from "@/components/LearnLayout";
import { FeatureStack } from "@/components/learn/FeatureStack";
import PriceCompareCarousel from "@/components/PriceCompareCarousel";
import {
  Search, Bell, TrendingDown, ArrowUpRight,
} from "lucide-react";

const HOW_STEPS = [
  { n: "01", title: "Search", desc: "Just search for the product you need. One search bar across every major sim-racing retailer." },
  { n: "02", title: "Narrow it down", desc: "Filter by category or brand if needed: wheel bases, steering wheels, pedals, cockpits and more." },
  { n: "03", title: "Compare live", desc: "Compare prices, stock and shipping in real time, side-by-side across every store." },
  { n: "04", title: "Click to buy", desc: "Found the best deal? Click straight through to the retailer and purchase." },
];

function SearchEverywhereBody() {
  const results = [
    { retailer: "Micro Center", stock: "In stock", best: true },
    { retailer: "MOZA Racing", stock: "In stock" },
    { retailer: "Amazon", stock: "In stock" },
  ];
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        One search bar, every major sim store and retailer.
      </p>
      <div className="mt-8 rounded-xl border border-border/70 bg-background/60 p-5 shadow-xl backdrop-blur">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-background/80 px-3 py-2">
          <Search className="h-4 w-4 text-primary" />
          <span className="text-sm text-foreground/80">MOZA R9</span>
          <span className="ml-auto rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-primary">12 results</span>
        </div>
        <div className="mt-3 space-y-2">
          {results.map((r) => (
            <div key={r.retailer} className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${
              r.best ? "border-primary/50 bg-primary/[0.06]" : "border-border bg-background/70"
            }`}>
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${r.best ? "text-foreground" : "text-foreground/80"}`}>{r.retailer}</span>
                {r.best && <span className="rounded-full border border-primary/50 bg-background px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-primary">Best price</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground/60">{r.stock}</span>
                <span className={`font-display text-sm font-bold tracking-[0.2em] ${r.best ? "text-primary" : "text-foreground/90"}`}>{r.best ? "$" : "$$"}</span>
              </div>
            </div>
          ))}
          {/* fading skeleton rows hint at many more results */}
          <div className="space-y-1.5 pt-0.5" aria-label="More results available">
            {[0.45, 0.28, 0.15].map((o, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-border/50 bg-background/40 px-3 py-2" style={{ opacity: o }}>
                <span className="block h-2 w-28 rounded-full bg-foreground/40" />
                <span className="block h-2 w-8 rounded-full bg-foreground/40" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RealTimePricingBody() {
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        See in-stock status, shipping, and current prices for each listing side by side. Everything you need to get the best deal.
      </p>
      <div className="mt-6 rounded-lg border border-border bg-background/60 p-4">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <span className="text-xs text-muted-foreground/70">Fanatec CSL DD+</span>
          <span className="flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            <TrendingDown className="h-3 w-3" />−5%
          </span>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="font-display text-2xl font-bold text-primary">$899</p>
            <p className="text-[10px] text-muted-foreground/60 line-through">$949</p>
            <p className="mt-1 text-[10px] font-medium text-emerald-400">In stock · Free shipping</p>
          </div>
          <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">Live</span>
        </div>
      </div>
    </div>
  );
}

function DeepLinkBody() {
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        One click takes you to the retailer product page and you check out on the retailer&apos;s site. We only give you the information you need to make a decision.
      </p>
      <div className="mt-6 rounded-xl border border-primary/50 bg-primary/[0.06] p-4 shadow-xl backdrop-blur">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">Pit Lane Sim Racing</span>
            <span className="rounded-full border border-primary/50 bg-background px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-primary">Best price</span>
          </div>
          <span className="text-[11px] text-muted-foreground/60">In stock</span>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="font-display text-xl font-bold tracking-[0.2em] text-primary">$</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground/60">Free shipping</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
            View at retailer <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

function AlertsBody() {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10">
        <Bell className="h-6 w-6 text-primary" />
      </div>
      <div>
        <p className="text-[0.963rem] leading-relaxed text-foreground/80">
          Ping me when the price drops or goes on sale.
        </p>
      </div>
    </div>
  );
}

function TrendsBody() {
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
      <div>
        <p className="max-w-2xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
          See how a product&apos;s price has moved in the past and buy at the right time.
        </p>
      </div>
      {/* Sparkline chart mock */}
      <div className="w-full min-w-[280px] rounded-xl border border-border/70 bg-background/60 p-4 md:w-[360px]">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">CSL DD+ · 90d</p>
            <p className="mt-1 font-display text-2xl font-bold text-emerald-400">$899</p>
          </div>
          <span className="flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            <TrendingDown className="h-3 w-3" />−5%
          </span>
        </div>
        <svg viewBox="0 0 320 80" className="mt-3 h-16 w-full" aria-hidden>
          <defs>
            <linearGradient id="pc-line" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 30 L40 25 L80 45 L120 20 L160 40 L200 32 L240 55 L280 50 L320 65 L320 80 L0 80 Z" fill="url(#pc-line)" />
          <path d="M0 30 L40 25 L80 45 L120 20 L160 40 L200 32 L240 55 L280 50 L320 65" stroke="#34d399" strokeWidth="2" fill="none" />
          <circle cx="320" cy="65" r="3" fill="#34d399" />
        </svg>
        <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-widest text-muted-foreground/50">
          <span>Aug</span><span>Sep</span><span>Oct</span><span>Now</span>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  {
    pos: "01",
    eyebrow: "// SEARCH",
    title: "Search everywhere at once",
    summary: "One search bar across every major retailer.",
    content: <SearchEverywhereBody />,
  },
  {
    pos: "02",
    eyebrow: "// PRICE",
    title: "Real-time pricing and status",
    summary: "Real current prices and stock, side-by-side.",
    content: <RealTimePricingBody />,
  },
  {
    pos: "03",
    eyebrow: "// LINK",
    title: "Straight to the retailer",
    summary: "One click to the retailer's product page.",
    content: <DeepLinkBody />,
  },
  {
    pos: "04",
    eyebrow: "// ALERTS",
    title: "Price watch",
    summary: "Get pinged when a product drops below your target.",
    content: <AlertsBody />,
  },
  {
    pos: "05",
    eyebrow: "// TRENDS",
    title: "Historic price trends",
    summary: "See how a product's price has moved over time.",
    content: <TrendsBody />,
  },
];

export default function LearnPriceCompare() {
  return (
    <LearnLayout
      testId="learn-price-compare"
      title="Price Compare"
      sub="Search and compare prices, stock, and shipping for sim-racing gear across all major sim stores and retailers from one place."
      heroImage="/banners/src-market-banner.jpg"
      heroBgPosition="75% 15%"
      bodyBg="#eceef1"
    >
      <section className="pl-12 sm:pl-14">
        <div className="relative z-20 inline-block -skew-x-[11deg] bg-primary px-4 py-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.65)] sm:px-5 sm:py-2.5">
          <h2 className="skew-x-[11deg] font-display text-[1.94rem] font-bold italic uppercase leading-none text-white sm:text-[2.43rem]">
            Why comparison matters
          </h2>
        </div>
        <div className="relative z-10 -mt-3">
          <FeatureStack items={FEATURES} testId="price-compare-feature-stack" tabBadge largeBody />
        </div>
      </section>

      <div className="mt-16">
        <PriceCompareCarousel fullBleed />
      </div>

      <section className="mt-20">
        <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase" style={{ color: "#0b0b0d" }}>
          <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
          How it works
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-4">
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="rounded-lg border border-border bg-card p-6">
              <span className="font-display text-5xl font-bold text-primary/70">{s.n}</span>
              <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-[0.99rem] sm:text-[1.1rem]" style={{ color: "#c7c8cf" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </LearnLayout>
  );
}
